import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { ReauthRequiredDialog } from 'widgets/auth';
import { useLogin, useLogout, useMe, useEntraLogin, authKeys } from 'hooks/auth';
import { clearAuth, getCSRFCookie } from 'api/base';
import { clearSessionId, getHeaderCsrfToken, getSessionId } from 'utils/authStorage';
import { getEnvVar, isElectron } from 'utils/environment';
import { type MSALConfig, MSALWrapper } from 'utils/msalWrapper';
import {
  notifySessionExpired,
  setSessionExpiryEnabled,
  subscribeToSessionExpiry,
} from 'utils/sessionExpiry';
import { clearStorageKeepingSurvivors, notifyLogoutCleanup } from 'utils/logoutCleanup';
import { useQueryClient } from '@tanstack/react-query';

const sanitizeAuthority = (authority: string | undefined, fallback: string): string => {
  const trimmed = authority?.trim();
  if (!trimmed) return fallback;
  const sanitized = trimmed.replace(/\/oauth2\/v2\.0\/authorize$/i, '');
  return sanitized || fallback;
};

const NATIVE_REDIRECT_URI =
  getEnvVar('VITE_ENTRA_ID_NATIVE_REDIRECT_URI', 'http://localhost') ?? 'http://localhost';

const createMsalConfig = (): MSALConfig => {
  const defaultClientId = getEnvVar('VITE_ENTRA_ID_CLIENT_ID') ?? '';
  const defaultTenantId = getEnvVar('VITE_ENTRA_ID_TENANT_ID') ?? '';
  const defaultAuthority = sanitizeAuthority(getEnvVar('VITE_ENTRA_ID_AUTHORITY'), '');

  if (!defaultClientId || !defaultTenantId || !defaultAuthority) {
    throw new Error('Missing Microsoft Entra configuration. Check VITE_ENTRA_ID_* env variables.');
  }

  if (isElectron) {
    const nativeClientId =
      getEnvVar('VITE_ENTRA_ID_NATIVE_CLIENT_ID', defaultClientId) ?? defaultClientId;
    const nativeAuthority = sanitizeAuthority(
      getEnvVar('VITE_ENTRA_ID_NATIVE_AUTHORITY'),
      defaultAuthority
    );
    const nativeTenantId =
      getEnvVar('VITE_ENTRA_ID_NATIVE_TENANT_ID', defaultTenantId) ?? defaultTenantId;

    return {
      auth: {
        clientId: nativeClientId,
        tenantId: nativeTenantId,
        authority: nativeAuthority,
        redirectUri: NATIVE_REDIRECT_URI,
      },
      cache: {
        cacheLocation: 'localStorage' as const,
        storeAuthStateInCookie: false,
      },
    };
  }

  const defaultRedirect =
    typeof window !== 'undefined' && window.location ? window.location.origin : '/';

  return {
    auth: {
      clientId: defaultClientId,
      tenantId: defaultTenantId,
      authority: defaultAuthority,
      redirectUri: defaultRedirect,
    },
    cache: {
      cacheLocation: 'localStorage' as const,
      storeAuthStateInCookie: false,
    },
  };
};

interface User {
  id: number;
  email: string;
  firstName: string;
  lastName: string;
}

enum AuthStatus {
  Idle = 'idle',
  Authenticating = 'authenticating',
  Authenticated = 'authenticated',
  Error = 'error',
}

enum AuthAction {
  Login = 'login',
  Refresh = 'refresh',
  Logout = 'logout',
  EntraLogin = 'entraLogin',
}

type EntraRefreshOutcome = 'success' | 'declined' | 'error';

interface AuthContextType {
  isAuthenticated: boolean;
  loading: boolean;
  user: User | null;
  authStatus: AuthStatus;
  authError: Error | null;
  lastAuthAction: AuthAction | null;
  login: (params: { email: string; password: string }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
  entraLogin: () => Promise<void>;
  refreshEntraSession: (options?: { force?: boolean }) => Promise<EntraRefreshOutcome>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const normalizeEmail = (value?: string | null): string | null => {
  const normalized = value?.trim().toLowerCase();
  return normalized || null;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const msalConfig = useMemo(() => createMsalConfig(), []);
  const navigate = useNavigate();
  const isHandlingSessionExpiryRef = useRef(false);
  const csrfCheckedForUserRef = useRef<number | null>(null);
  const entraRefreshRef = useRef<{
    email: string | null;
    promise: Promise<EntraRefreshOutcome>;
  } | null>(null);
  const userEmailRef = useRef<string | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [authStatus, setAuthStatus] = useState<AuthStatus>(AuthStatus.Idle);
  const [authError, setAuthError] = useState<Error | null>(null);
  const [lastAuthAction, setLastAuthAction] = useState<AuthAction | null>(null);
  const [reauthRequired, setReauthRequired] = useState(false);
  const { mutateAsync: loginMutation } = useLogin();
  const { mutateAsync: logoutMutation } = useLogout();
  const { mutateAsync: entraLoginMutation } = useEntraLogin();
  const { data: me, error, refetch: refetchMe, isLoading: isMeLoading } = useMe();
  const queryClient = useQueryClient();
  userEmailRef.current = user?.email ?? null;

  const cleanupClientAuth = async () => {
    try {
      queryClient.clear();
    } catch {
      /* empty */
    }

    setUser(null);
    setAuthStatus(AuthStatus.Idle);
    setAuthError(null);
    csrfCheckedForUserRef.current = null;
    clearAuth();

    entraRefreshRef.current = null;
    notifyLogoutCleanup();

    if (isElectron) {
      await clearSessionId();
    } else {
      document.cookie = 'csrftoken=; Max-Age=0; path=/; secure; samesite=strict;';
      document.cookie = 'sessionid=; Max-Age=0; path=/; secure; samesite=strict;';
    }

    try {
      clearStorageKeepingSurvivors(window.localStorage);
      window.sessionStorage?.clear?.();
    } catch {
      /* empty */
    }
  };

  const refresh = useCallback(async (): Promise<void> => {
    setSessionExpiryEnabled(true);
    setAuthStatus(AuthStatus.Authenticating);
    setLastAuthAction(AuthAction.Refresh);
    setAuthError(null);
    try {
      if (isElectron) {
        const sessionId = await getSessionId();
        if (!sessionId) {
          setUser(null);
          setAuthStatus(AuthStatus.Error);
          setAuthError(new Error('No valid session found'));
          await clearSessionId();
          clearAuth();
          queryClient.setQueryData(authKeys.me, null);
          await queryClient.invalidateQueries({ queryKey: authKeys.me });
          return;
        }
      } else {
        // For web browsers, ensure CSRF token is available
        await getCSRFCookie();
      }

      const meData = await refetchMe();
      setUser(meData.data ?? null);
      setAuthStatus(AuthStatus.Authenticated);
    } catch (error) {
      setUser(null);
      setAuthStatus(AuthStatus.Error);
      setAuthError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [refetchMe, queryClient]);

  useEffect(() => {
    if (me && !error) {
      setUser(me);
      setAuthStatus(AuthStatus.Authenticated);
    } else if (error) {
      setAuthStatus(AuthStatus.Error);
      setAuthError(error instanceof Error ? error : new Error(String(error)));
    }
  }, [me, error, isMeLoading]);

  useEffect(() => {
    if (!isElectron || !me || error) return;
    if (csrfCheckedForUserRef.current === me.id) return;
    csrfCheckedForUserRef.current = me.id;

    let active = true;
    void getHeaderCsrfToken().then((csrfToken) => {
      if (active && !csrfToken) {
        console.warn('Authenticated session is missing its CSRF token; re-login required.');
        setReauthRequired(true);
      }
    });
    return () => {
      active = false;
    };
  }, [me, error]);

  const login = async ({ email, password }: { email: string; password: string }) => {
    setAuthStatus(AuthStatus.Authenticating);
    setLastAuthAction(AuthAction.Login);
    setAuthError(null);
    try {
      entraRefreshRef.current = null;
      await loginMutation({ email, password });
      await refresh();
      setLastAuthAction(AuthAction.Login);
    } catch (error) {
      setAuthStatus(AuthStatus.Error);
      setAuthError(error instanceof Error ? error : new Error(String(error)));
      throw error; // Re-throw so login screen can handle it
    }
  };

  const logout = async () => {
    setSessionExpiryEnabled(false);
    setLastAuthAction(AuthAction.Logout);
    setAuthError(null);
    try {
      await logoutMutation();
    } catch (error) {
      const err = error instanceof Error ? error : new Error(String(error));
      setAuthStatus(AuthStatus.Error);
      setAuthError(err);
    } finally {
      await cleanupClientAuth();
    }
  };

  useEffect(() => {
    return subscribeToSessionExpiry(() => {
      if (isHandlingSessionExpiryRef.current) return;
      isHandlingSessionExpiryRef.current = true;
      void cleanupClientAuth()
        .then(() => {
          void navigate('/login?sessionExpired=true', { replace: true });
        })
        .finally(() => {
          isHandlingSessionExpiryRef.current = false;
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (isElectron) {
      return;
    }

    let canceled = false;

    void (async () => {
      try {
        const msalInstance = MSALWrapper.getInstance(msalConfig);
        if (canceled) return;

        await msalInstance.initialize();
        const result = await msalInstance.handleRedirectPromise();
        if (canceled) return;

        if (result?.idToken) {
          setLastAuthAction(AuthAction.EntraLogin);
          setAuthStatus(AuthStatus.Authenticating);
          setAuthError(null);
          await entraLoginMutation({ idToken: result.idToken });
          await refresh();
          setLastAuthAction(AuthAction.EntraLogin);
        }
      } catch (err) {
        setAuthStatus(AuthStatus.Error);
        setAuthError(err instanceof Error ? err : new Error(String(err)));
      }
    })();

    return () => {
      canceled = true;
    };
  }, [refresh, entraLoginMutation, msalConfig]);

  // Re-mints the Entra cookies AMBOSS SSO federates against. Semantics:
  // docs/decisions/VAA-800.md.
  const refreshEntraSession = useCallback(
    (options?: { force?: boolean }): Promise<EntraRefreshOutcome> => {
      if (!isElectron) return Promise.resolve('success');
      const expectedEmail = normalizeEmail(userEmailRef.current);
      const cached = entraRefreshRef.current;
      if (options?.force || !cached || cached.email !== expectedEmail) {
        const promise = (async (): Promise<EntraRefreshOutcome> => {
          const request = {
            clientId: msalConfig.auth.clientId,
            authority: msalConfig.auth.authority,
            scopes: ['openid', 'profile', 'email'],
            loginHint: expectedEmail ?? undefined,
          };
          const matchesExpected = (username?: string | null) =>
            !expectedEmail || normalizeEmail(username) === expectedEmail;
          try {
            let result = await window.electronAPI?.entraLogin?.({
              ...request,
              forceInteractive: options?.force,
            });
            if (result?.idToken && !matchesExpected(result.account?.username)) {
              console.warn('Entra session belongs to a different account; re-authenticating');
              await window.electronAPI?.entraLogout?.();
              await window.electronAPI?.clearEntraSession?.();
              result = await window.electronAPI?.entraLogin?.({
                ...request,
                forceInteractive: true,
              });
              if (result?.idToken && !matchesExpected(result.account?.username)) {
                console.warn('Entra re-authentication yielded a different account; not federating');
                return 'declined';
              }
            }
            return result?.idToken ? 'success' : 'declined';
          } catch (error) {
            console.error('Entra session refresh failed', error);
            return 'error';
          }
        })().then((outcome) => {
          if (outcome === 'error' && entraRefreshRef.current?.promise === promise) {
            entraRefreshRef.current = null;
          }
          return outcome;
        });
        entraRefreshRef.current = { email: expectedEmail, promise };
        return promise;
      }
      return cached.promise;
    },
    [msalConfig]
  );

  const entraLogin = async (): Promise<void> => {
    // Prevent multiple concurrent login attempts
    if (authStatus === AuthStatus.Authenticating) {
      return;
    }

    setLastAuthAction(AuthAction.EntraLogin);
    setAuthStatus(AuthStatus.Authenticating);
    setAuthError(null);

    const scopes = ['openid', 'profile', 'email'];

    if (isElectron) {
      try {
        if (!window.electronAPI?.entraLogin) {
          throw new Error('Microsoft login is not available in the Electron environment.');
        }

        entraRefreshRef.current = null;

        const payload = {
          clientId: msalConfig.auth.clientId,
          authority: msalConfig.auth.authority,
          scopes,
          prompt: 'select_account',
        };

        const result = await window.electronAPI.entraLogin(payload);
        if (!result?.idToken) {
          setAuthStatus(AuthStatus.Idle);
          return;
        }

        await entraLoginMutation({ idToken: result.idToken });
        entraRefreshRef.current = {
          email: normalizeEmail(result.account?.username),
          promise: Promise.resolve('success'),
        };
        await refresh();
        setLastAuthAction(AuthAction.EntraLogin);
      } catch (error) {
        console.error('Electron Microsoft login failed', error);
        setAuthStatus(AuthStatus.Error);
        setAuthError(error instanceof Error ? error : new Error(String(error)));
      }
      return;
    }

    try {
      const msalInstance = MSALWrapper.getInstance(msalConfig);
      await msalInstance.loginRedirect({
        scopes,
        prompt: 'select_account',
        authority: msalConfig.auth.authority,
        redirectUri: msalConfig.auth.redirectUri,
      });
    } catch (error) {
      setAuthStatus(AuthStatus.Error);
      setAuthError(error instanceof Error ? error : new Error(String(error)));
    }
  };

  const value: AuthContextType = {
    isAuthenticated: !!me && !error,
    loading: isMeLoading,
    user,
    authStatus,
    authError,
    lastAuthAction,
    login,
    logout,
    refresh,
    entraLogin,
    refreshEntraSession,
  };

  const confirmReauth = () => {
    setReauthRequired(false);
    notifySessionExpired();
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <ReauthRequiredDialog isOpen={reauthRequired} onConfirm={confirmReauth} />
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export { AuthStatus, AuthAction };
